import {
  ActivityIcon,
  FileTextIcon,
  Link2Icon,
  ListTreeIcon,
  HistoryIcon,
  SlidersHorizontalIcon,
  type LucideIcon,
} from "lucide-react";
import type { KeyboardEvent } from "react";

import { cn } from "~/lib/utils";

export type ProjectTaskMobileSection =
  | "description"
  | "subtasks"
  | "dependencies"
  | "activity"
  | "history"
  | "details";

interface MobileTaskTabDefinition {
  readonly id: ProjectTaskMobileSection;
  readonly label: string;
  readonly icon: LucideIcon;
}

const MOBILE_TASK_TABS: readonly MobileTaskTabDefinition[] = [
  { id: "description", label: "Description", icon: FileTextIcon },
  { id: "subtasks", label: "Subtasks", icon: ListTreeIcon },
  { id: "dependencies", label: "Dependencies", icon: Link2Icon },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "history", label: "History", icon: HistoryIcon },
  { id: "details", label: "Details", icon: SlidersHorizontalIcon },
];

export interface ProjectTaskMobileTabsProps {
  readonly taskId: number;
  readonly section: ProjectTaskMobileSection;
  readonly onSectionChange: (section: ProjectTaskMobileSection) => void;
  readonly sections: Readonly<Record<ProjectTaskMobileSection, React.ReactNode>>;
}

export function ProjectTaskMobileTabs(props: ProjectTaskMobileTabsProps) {
  const selectTabAtIndex = (event: KeyboardEvent<HTMLButtonElement>, nextIndex: number) => {
    const nextTab = MOBILE_TASK_TABS[nextIndex];
    if (!nextTab) return;
    event.preventDefault();
    props.onSectionChange(nextTab.id);
    const tabList = event.currentTarget.parentElement;
    const nextButton = tabList?.querySelector<HTMLButtonElement>(`#task-mobile-tab-${nextTab.id}`);
    nextButton?.focus();
    nextButton?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const selectRelativeTab = (event: KeyboardEvent<HTMLButtonElement>, offset: number) => {
    const currentIndex = MOBILE_TASK_TABS.findIndex((tab) => tab.id === props.section);
    const nextIndex = (currentIndex + offset + MOBILE_TASK_TABS.length) % MOBILE_TASK_TABS.length;
    selectTabAtIndex(event, nextIndex);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:hidden" data-task-mobile-tabs={props.taskId}>
      <div
        role="tablist"
        aria-label="Task sections"
        className="flex shrink-0 snap-x scroll-px-4 gap-[8px] overflow-x-auto border-b border-border bg-card px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:px-5"
      >
        {MOBILE_TASK_TABS.map((tab) => {
          const selected = tab.id === props.section;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              id={`task-mobile-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`task-mobile-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={(event) => {
                props.onSectionChange(tab.id);
                event.currentTarget.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight") selectRelativeTab(event, 1);
                if (event.key === "ArrowLeft") selectRelativeTab(event, -1);
                if (event.key === "Home") selectTabAtIndex(event, 0);
                if (event.key === "End") selectTabAtIndex(event, MOBILE_TASK_TABS.length - 1);
              }}
              className={cn(
                "relative inline-flex min-h-touch shrink-0 snap-start items-center gap-2 px-2 text-sm font-medium transition-colors after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full",
                selected
                  ? "text-foreground after:bg-primary"
                  : "text-muted-foreground after:bg-transparent hover:text-foreground",
              )}
            >
              <Icon className="size-4" aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>
      {MOBILE_TASK_TABS.map((tab) => (
        <div
          key={tab.id}
          id={`task-mobile-panel-${tab.id}`}
          role={tab.id === props.section ? "tabpanel" : undefined}
          aria-labelledby={tab.id === props.section ? `task-mobile-tab-${tab.id}` : undefined}
          tabIndex={tab.id === props.section ? 0 : -1}
          hidden={tab.id !== props.section}
          className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-4 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:px-5"
        >
          {props.sections[tab.id]}
        </div>
      ))}
    </div>
  );
}
